import { trialDays } from '@listening/frontend/pricing';
import dashboard from '@listening/assets/astro/images/new-design-25/ui/try-listening-banner-dashboard.png';
import mobileView from '@listening/assets/astro/images/new-design-25/ui/try-listening-banner-mobileview.png';
import qrAppStore from '@listening/assets/astro/images/new-design-25/ui/try-listening-banner-qr_appstore.png';
import qrGooglePlay from '@listening/assets/astro/images/new-design-25/ui/try-listening-banner-qr_google.png';

import { Button } from '@listening/frontend/components/radix/button';
import { useCtaHref } from '@/components/astro/cta-button/cta-button.tsx';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import * as m from '@/paraglide/messages';

const downloadOptions = [
  {
    store: 'App Store',
    platform: 'iOS',
    qrCode: qrAppStore,
    qrAlt: 'App Store QR Code',
    href: 'https://apps.apple.com/us/app/listening-text-to-speech/id1635430768',
  },
  {
    store: 'Google Play',
    platform: 'Android',
    qrCode: qrGooglePlay,
    qrAlt: 'Google Play QR Code',
    href: 'https://play.google.com/store/apps/details?id=io.listening.main',
  },
];

export function TryListeningBanner() {
  const { isInFreePlanCountry } = useFreePlan();
  const ctaHref = useCtaHref();

  return (
    <section
      aria-labelledby='try-listening-title'
      className='relative w-full overflow-hidden bg-linear-to-br from-[#5b8cff] via-[#7ba2ff] to-[#a8c0ff]'
    >
      <div className='mx-auto w-full max-w-[1600px]'>
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-0 xl:gap-12'>
          <div className='flex flex-col justify-center p-6 sm:p-8 md:p-16 lg:px-20 xl:px-24'>
            <div className='flex flex-col text-white'>
              <div className='space-y-3 sm:space-y-4'>
                <h2
                  className='text-3xl leading-tight font-bold sm:text-4xl md:text-5xl lg:text-6xl'
                  id='try-listening-title'
                >
                  {m.try_banner_title_free()}
                  {/* Free-plan countries get a free tier, not a trial —
                      only everyone else is promised days. */}
                  {!isInFreePlanCountry && (
                    <>
                      {' '}
                      <br className='hidden sm:block' />{' '}
                      {m.try_banner_title_free_days({
                        trialDays: String(trialDays),
                      })}
                    </>
                  )}
                </h2>
              </div>

              <Button
                asChild
                className='mt-6 w-32 rounded-3xl bg-white py-2.5 text-sm font-semibold text-[#0f0e0c] hover:bg-white/90 sm:mt-8 sm:py-3 sm:text-base md:py-6'
              >
                <a
                  aria-label={
                    isInFreePlanCountry
                      ? m.common_try_for_free()
                      : m.try_banner_cta()
                  }
                  href={ctaHref}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {isInFreePlanCountry
                    ? m.common_try_for_free()
                    : m.try_banner_cta()}
                </a>
              </Button>
            </div>

            <div
              aria-label='Download options'
              className='mt-4 flex flex-row gap-4'
              role='group'
            >
              {downloadOptions.map(
                ({ store, platform, qrCode, qrAlt, href }) => (
                  <a
                    aria-label={`Download for ${platform} from ${store}`}
                    className='flex flex-1 flex-col items-center gap-2 rounded-xl bg-white/10 p-2.5 transition-all duration-300 hover:bg-white/15 sm:gap-3 sm:p-3 lg:flex-row lg:items-start'
                    href={href}
                    key={href}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    <div className='shrink-0 rounded-lg p-1.5 sm:p-2'>
                      <img
                        alt={qrAlt}
                        className='size-12 sm:size-10 md:size-16'
                        src={qrCode}
                      />
                    </div>
                    <div className='space-y-1.5 text-center lg:text-left'>
                      <p className='text-xs font-bold text-white sm:text-base md:text-lg lg:text-[10px] xl:text-base'>
                        Download from {store}
                      </p>
                      <p className='text-[10px] text-white/90 sm:text-sm md:text-base lg:text-xs xl:text-lg'>
                        Download for {platform}
                      </p>
                    </div>
                  </a>
                ),
              )}
            </div>
          </div>

          <div
            aria-hidden='true'
            className='hidden items-center justify-end lg:flex'
          >
            <div className='relative my-10'>
              <img
                alt='Preview of the Listening app dashboard interface'
                className='h-auto w-full max-w-[400px] object-contain xl:max-w-[500px]'
                height={600}
                src={dashboard}
                width={800}
              />
              <img
                alt='Preview of the Listening app mobile interface'
                className='absolute bottom-[-29%] left-[-10%] hidden w-[28%] -translate-y-1/2 transform object-contain xl:block'
                height={300}
                src={mobileView}
                width={400}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
